const STORAGE_KEY = "incite:active-chat-thread:v1";
const ACTIVE_THREAD_TTL_MS = 60 * 60 * 1000;

const now = () => Date.now();

const storage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const ownerKeyForScope = (scope) => {
  if (!scope) return null;

  const userId = scope.user_id ?? scope.userId ?? null;
  if (userId) return `user:${userId}`;

  const sessionId =
    scope.uuid ??
    scope.preAccountUuid ??
    scope.current_session_id ??
    scope.sessionId ??
    null;
  if (sessionId) return `session:${sessionId}`;

  return null;
};

const storageKeyForScope = (scope) => {
  const ownerKey = ownerKeyForScope(scope);
  return ownerKey ? `${STORAGE_KEY}:${ownerKey}` : STORAGE_KEY;
};

const normalizeThreadSnapshot = (thread, scope) => {
  const threadUuid = thread?.threadUuid || thread?.thread_uuid || null;
  if (!threadUuid) return null;

  const snapshot = {
    threadUuid,
    title: thread?.title || thread?.threadTitle || "Chat",
    savedAt: now(),
  };

  const ownerKey = ownerKeyForScope(scope);
  if (ownerKey) snapshot.ownerKey = ownerKey;

  return snapshot;
};

export const isTempThreadUuid = (threadUuid) =>
  typeof threadUuid === "string" && threadUuid.startsWith("temp-");

export const rememberActiveChatThread = (thread, scope = null) => {
  const snapshot = normalizeThreadSnapshot(thread, scope);
  const store = storage();
  if (!snapshot || !store) return null;

  try {
    const key = storageKeyForScope(scope);
    store.setItem(key, JSON.stringify(snapshot));
    if (key !== STORAGE_KEY) store.removeItem(STORAGE_KEY);
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }

  return snapshot;
};

export const clearRememberedActiveChatThread = (scope = null) => {
  const store = storage();
  if (!store) return;

  try {
    const scopedKey = storageKeyForScope(scope);
    store.removeItem(scopedKey);
    store.removeItem(STORAGE_KEY);

    if (!ownerKeyForScope(scope)) {
      for (let i = store.length - 1; i >= 0; i -= 1) {
        const key = store.key(i);
        if (key?.startsWith(`${STORAGE_KEY}:`)) {
          store.removeItem(key);
        }
      }
    }
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }
};

export const readRememberedActiveChatThread = (scope = null) => {
  const store = storage();
  if (!store) return null;

  try {
    const key = storageKeyForScope(scope);
    const raw = store.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.threadUuid || !parsed?.savedAt) {
      store.removeItem(key);
      return null;
    }

    const ownerKey = ownerKeyForScope(scope);
    if (ownerKey && parsed.ownerKey && parsed.ownerKey !== ownerKey) {
      store.removeItem(key);
      return null;
    }

    if (now() - Number(parsed.savedAt) > ACTIVE_THREAD_TTL_MS) {
      store.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    try {
      store.removeItem(storageKeyForScope(scope));
    } catch {
      // localStorage can be unavailable in private or restricted contexts.
    }
    return null;
  }
};

export { ACTIVE_THREAD_TTL_MS };
